import { ChatMessage, ChatbotState } from '../states/chatbot'
import { ChatbotService } from '../../services/services'
import { Dispatch } from 'redux'

declare global {
	interface Window {
		currentReader: ReadableStreamDefaultReader<Uint8Array> | null
	}
}

export const APPEND_SIMPLE_BOT_MESSAGE = 'APPEND_SIMPLE_BOT_MESSAGE'
export const UPDATE_STREAMING_BOT_MESSAGE = 'UPDATE_STREAMING_BOT_MESSAGE'
export const END_BOT_STREAMING = 'END_BOT_STREAMING'
export const SET_BOT_TYPING = 'SET_BOT_TYPING'
export const SET_CONVERSATION_ID = 'SET_CONVERSATION_ID'
export const STOP_STREAMING = 'STOP_STREAMING'
export const RESET_CONVERSATION = 'RESET_CONVERSATION'

export type SendMessagePayload = ChatMessage
export type UpdateStreamingPayload = {
	id: string
	text: string
	isTyping: boolean
}
export type EndStreamingPayload = { id: string; text: string }
export type SetTypingPayload = boolean
export type SetConversationIdPayload = string
export type StopStreamingPayload = { id: string; text: string } | undefined
export type ResetConversationPayload = undefined

export interface SendMessageAction {
	type: typeof APPEND_SIMPLE_BOT_MESSAGE
	payload: SendMessagePayload
}

export interface UpdateStreamingBotMessageAction {
	type: typeof UPDATE_STREAMING_BOT_MESSAGE
	payload: UpdateStreamingPayload
}

export interface EndBotStreamingAction {
	type: typeof END_BOT_STREAMING
	payload: EndStreamingPayload
}

export interface SetBotTypingAction {
	type: typeof SET_BOT_TYPING
	payload: SetTypingPayload
}

export interface SetConversationIdAction {
	type: typeof SET_CONVERSATION_ID
	payload: SetConversationIdPayload
}

export interface StopStreamingAction {
	type: typeof STOP_STREAMING
	payload?: StopStreamingPayload
}

export interface ResetConversationAction {
	type: typeof RESET_CONVERSATION
	payload?: ResetConversationPayload
}

export type ChatbotActionTypes =
	| SendMessageAction
	| UpdateStreamingBotMessageAction
	| EndBotStreamingAction
	| SetBotTypingAction
	| SetConversationIdAction
	| StopStreamingAction
	| ResetConversationAction

export const initializeConversation = () => {
	return async (
		dispatch: Dispatch<ChatbotActionTypes>,
		getState: () => { chatbot: ChatbotState },
		language: string
	) => {
		const state = getState()
		if (!state?.chatbot?.conversation_id) {
			try {
				const response = await ChatbotService.startConversation(
					language
				)
				dispatch({
					type: SET_CONVERSATION_ID,
					payload: response.conversation_id,
				})
				dispatch({
					type: APPEND_SIMPLE_BOT_MESSAGE,
					payload: {
						id: Date.now().toString(),
						text: response.answer.text,
						role: 'bot',
					},
				})
			} catch (error) {
				console.error('Error starting conversation:', error)
			}
		}
	}
}

export const sendChatMessage = (
	text: string,
	language: string,
	conversationId: string,
	username: string
) => {
	return async (dispatch: Dispatch) => {
		const botMessageId = Date.now().toString()

		dispatch({
			type: APPEND_SIMPLE_BOT_MESSAGE,
			payload: {
				id: (Date.now() - 1).toString(),
				text,
				role: 'user',
			},
		})

		dispatch({
			type: APPEND_SIMPLE_BOT_MESSAGE,
			payload: {
				id: botMessageId,
				text: '',
				role: 'bot',
				isTyping: true,
			},
		})

		dispatch({
			type: SET_BOT_TYPING,
			payload: true,
		})

		try {
			const response = await ChatbotService.sendStreamingMessage(
				text,
				language,
				conversationId,
				username
			)

			if (!response || !response.data) {
				console.warn(
					'Response or response.data is undefined, using alternative mock approach'
				)
				const mockText =
					'Questa è una risposta di fallback generata localmente. Il sistema di streaming non è disponibile al momento.'
				const words = mockText.split(' ')
				let accumulatedText = ''

				for (const word of words) {
					await new Promise((resolve) => setTimeout(resolve, 200))
					accumulatedText += word + ' '
					dispatch({
						type: UPDATE_STREAMING_BOT_MESSAGE,
						payload: {
							id: botMessageId,
							text: accumulatedText,
							isTyping: true,
						},
					})
				}

				dispatch({
					type: END_BOT_STREAMING,
					payload: {
						id: botMessageId,
						text: accumulatedText,
					},
				})
				dispatch({
					type: SET_BOT_TYPING,
					payload: false,
				})
				return
			}

			const lines = response.data.split('\n')
			let accumulatedText = ''

			for (const line of lines) {
				if (line.startsWith('data: ')) {
					try {
						const data = JSON.parse(line.substring(6))

						if (data.reference_documents) {
							continue
						}

						if (data.answer && data.answer.text) {
							accumulatedText += data.answer.text
							dispatch({
								type: UPDATE_STREAMING_BOT_MESSAGE,
								payload: {
									id: botMessageId,
									text: accumulatedText,
									isTyping: false,
								},
							})
						}
					} catch (e) {
						console.error('Error parsing streaming data:', e)
					}
				}
			}

			dispatch({
				type: END_BOT_STREAMING,
				payload: {
					id: botMessageId,
					text: accumulatedText,
				},
			})
			dispatch({
				type: SET_BOT_TYPING,
				payload: false,
			})
		} catch (error) {
			console.error('Error sending message:', error)
			dispatch({
				type: END_BOT_STREAMING,
				payload: {
					id: botMessageId,
					text: 'Mi dispiace, si è verificato un errore.',
				},
			})
			dispatch({
				type: SET_BOT_TYPING,
				payload: false,
			})
		}
	}
}

export const stopStreaming = (messageId: string, currentText: string) => {
	return (dispatch: Dispatch) => {
		if (window.currentReader) {
			window.currentReader.cancel()
			window.currentReader = null
		}

		dispatch({
			type: STOP_STREAMING,
			payload: {
				id: messageId,
				text: currentText,
			},
		})
		dispatch({
			type: SET_BOT_TYPING,
			payload: false,
		})
	}
}

export const resetConversation = () => {
	return async (
		dispatch: Dispatch<ChatbotActionTypes>,
		getState: () => { chatbot: ChatbotState },
		language: string
	) => {
		dispatch({
			type: RESET_CONVERSATION,
		})

		await initializeConversation()(dispatch, getState, language)
	}
}
